import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { Typography } from '@mui/joy';

/**
 * Loading placeholders for the Hero dashboards. Skeletons match the dimensions of
 * the content they stand in for, so the swap causes no layout shift; loaded content
 * fades in via `fadeInSx` to mask the blink on fast connections.
 */

/** Short opacity fade for content that replaces a skeleton. Spread into the wrapper's sx. */
export const fadeInSx = {
    '@keyframes heroFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
    animation: 'heroFadeIn 150ms ease-in',
} as const;

/**
 * Mirrors the HeroLead block: icon circle + caption / headline / sub lines.
 * Uses the same Typography levels and spacing as the real lead so the skeleton
 * occupies the exact same height and the banner doesn't grow on reveal.
 * `withMetaLine` adds a fourth line matching the connected lead's firmware row.
 */
export const HeroLeadSkeleton = ({ compact, withMetaLine }: { compact?: boolean; withMetaLine?: boolean }) => {
    const iconBox = compact ? 48 : 64;
    const headline: 'titleLarge' | 'titleExtraLarge' = compact ? 'titleLarge' : 'titleExtraLarge';
    return (
        <>
            <Box sx={{ display: 'flex', width: iconBox, height: iconBox, alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <Skeleton variant="circular" width={iconBox} height={iconBox} />
            </Box>
            <Box>
                <Typography level="titleSmallLight">
                    <Skeleton width={100} />
                </Typography>
                <Typography level={headline}>
                    <Skeleton width={190} />
                </Typography>
                <Typography level="bodySmall" sx={{ mt: '8px' }}>
                    <Skeleton width={250} />
                </Typography>
                {withMetaLine && (
                    <Typography level="bodySmall" sx={{ mt: '6px' }}>
                        <Skeleton width={210} />
                    </Typography>
                )}
            </Box>
        </>
    );
};

/**
 * Button-shaped placeholders for the Hero actions row. The row is composed from
 * several independently loading sources (connect gate, commissioning, event
 * shortcuts, RMA); the dashboard shows this skeleton until all of them have
 * answered, so the buttons appear once, in their final composition, instead of
 * popping in one by one. Heights match the Joy `md` buttons of the real row.
 */
export const HeroActionsSkeleton = () => (
    <Box data-testid="hero-actions-skeleton" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', flex: 1 }}>
        {[168, 150, 140, 130].map((width, index) => (
            <Skeleton key={index} variant="rounded" width={width} height={36} sx={{ borderRadius: '6px' }} />
        ))}
    </Box>
);

/** Two pill chips holding the network-info slot of a connected lead while it loads. */
export const NetworkInfoSkeleton = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Skeleton variant="rounded" width={88} height={18} sx={{ borderRadius: '9px' }} />
        <Skeleton variant="rounded" width={64} height={18} sx={{ borderRadius: '9px' }} />
    </Box>
);

/** Two pill chips holding the firmware line of a connected lead while it loads. */
export const FirmwareInfoSkeleton = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Skeleton variant="rounded" width={150} height={18} sx={{ borderRadius: '9px' }} />
        <Skeleton variant="rounded" width={190} height={18} sx={{ borderRadius: '9px' }} />
    </Box>
);

/**
 * Content-shaped placeholder rows for card bodies.
 * 'row' mirrors HeroListRow (icon square + two text lines), 'timeline' mirrors the
 * event history timeline (ringed dot + connector), 'text' is plain text lines.
 */
export const SkeletonListRows = ({
    rows = 3,
    variant = 'row',
}: {
    rows?: number;
    variant?: 'row' | 'timeline' | 'text';
}) => {
    if (variant === 'timeline') {
        return (
            <Box sx={{ pl: '6px', pt: '12px' }}>
                {Array.from({ length: rows }, (_, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: '12px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Skeleton variant="circular" width={12} height={12} sx={{ flex: 'none', mt: '3px' }} />
                            {index < rows - 1 && <Box sx={{ width: 2, flex: 1, backgroundColor: '#f0f0f0', my: '2px' }} />}
                        </Box>
                        <Box sx={{ pb: '16px', flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Skeleton width="35%" height={20} />
                                <Skeleton width={70} height={16} sx={{ ml: 'auto' }} />
                            </Box>
                            <Skeleton width="55%" height={16} />
                        </Box>
                    </Box>
                ))}
            </Box>
        );
    }

    if (variant === 'text') {
        return (
            <Box sx={{ pt: '6px' }}>
                <Skeleton width="45%" height={22} />
                <Skeleton width="70%" height={18} />
                <Skeleton width="60%" height={18} />
            </Box>
        );
    }

    return (
        <Box>
            {Array.from({ length: rows }, (_, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '9px', borderTop: '1px solid #eee' }}>
                    <Skeleton variant="rounded" width={28} height={28} sx={{ flex: 'none', borderRadius: '6px' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Skeleton width="45%" height={18} />
                        <Skeleton width="30%" height={15} />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};
